import { useState, useEffect, useCallback } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import apiService from "../../service/apiService";
import { FaRegEdit, FaEye, FaTrash } from "react-icons/fa";
import { Pagination } from "react-bootstrap";
import '../../assets/styles/cropmanage.css';
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";

const User = () => {
  // Essential state variables only
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [gender, setGender] = useState('');
  const [alldata, setAllData] = useState([]);
  const [totalItems, setTotalitems] = useState(0);
  const [totalpage, setTotalpage] = useState(0);
  const [deletedata, setDeleteData] = useState([]);
  const [genders, setGenders] = useState([]);
  
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const response = await apiService.post("/users/get-all", {
        userName: userName || "",
        email: email || "",
        contactNumber: contactNumber || "",
        aadhaarNumber: aadhaarNumber || "",
        gender: gender,
        pageNumber: page,
        pageSize: pageSize,
      });

      const data = response.data.body.data;
      setAllData(data);

      const formattedData = data.map((item) => ({
        id: item.userId,
        userName: item.userName,
        email: item.email,
        contactNumber: item.contactNumber,
        gender: item.gender,
        roleName: item.roleName,
        userId: item.userId,
        aadhaarNumber: item.aadhaarNumber
      }));

      setRows(formattedData);
      setTotalitems(response.data.body.recordsCount);
      setTotalpage(response.data.body.pageCount);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setAllData([]);
      setTotalitems(0);
      setTotalpage(0);
    }
  };

  const debouncedFetchData = useCallback(debounce(fetchData, 500), [
    userName, email, contactNumber, aadhaarNumber, gender, page, pageSize
  ]);

  useEffect(() => {
    debouncedFetchData();
    return () => debouncedFetchData.cancel();
  }, [debouncedFetchData]);

  const fetchGenders = async () => {
    try {
      const response = await apiService.get("/master/get-all-gender");
      const genderList = response.data.map(item => ({
        genderId: item.genderId,
        gender: item.gender,
      }));
      setGenders(genderList);
    } catch (error) {
      console.error("Error fetching genders:", error);
    }
  };

  useEffect(() => {
    fetchGenders();
  }, []);

  const handleEdit = (userId) => {
    navigate(`/edituser/${userId}`);
  };

  const handleView = (data) => {
    setSelectedRow(data);
    setOpen(true);
  };

  const handleDeleteClick = (data) => {
    setDeleteData(data);
    setOpenDelete(true);
  };

  const handleAdd = () => {
    navigate('/adduser');
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleDelete = async () => {
    try {
      const userId = deletedata.userId;
      await apiService.delete(`/users/delete/${userId}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting data:", error);
    } finally {
      setOpenDelete(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const renderPaginationItems = () => {
    const paginationItems = [];
    const maxPagesToShow = 7;
    const startPages = 3;
    const endPages = 2;

    if (totalpage <= maxPagesToShow) {
      for (let pages = 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    } else {
      for (let pages = 1; pages <= startPages; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      if (page > startPages + 1) {
        paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }

      const middlePages = [];
      const startMiddlePage = Math.max(page - 1, startPages + 1);
      const endMiddlePage = Math.min(page + 1, totalpage - endPages);

      for (let pages = startMiddlePage; pages <= endMiddlePage; pages++) {
        middlePages.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }

      paginationItems.push(...middlePages);

      if (page < totalpage - endPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }

      for (let pages = totalpage - endPages + 1; pages <= totalpage; pages++) {
        paginationItems.push(
          <Pagination.Item key={pages} active={page === pages} onClick={() => handlePageChange(pages)}>
            {pages}
          </Pagination.Item>
        );
      }
    }
    return paginationItems;
  };

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  return (
    <Box m="2px">
      <div style={{ maxHeight: "100vh", overflowY: "auto" }}>
        {/* Header - Same design as distdata */}
        <div style={{
          backgroundColor: "#ffffff",
          borderBottom: "3px solid #007bff",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}>
          <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center py-3 px-4">
              <div className="d-flex align-items-center">
                <div>
                  <h1 style={{
                    fontSize: "1.8rem",
                    fontWeight: "600",
                    color: "#2c3e50",
                    margin: "0"
                  }}>
                    User Management
                  </h1>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Button
                  variant="contained"
                  onClick={handleAdd}
                  style={{
                    backgroundColor: "#28a745",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: "500",
                    textTransform: "none"
                  }}
                >
                  Add User
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="mx-2" style={{ minHeight: "75vh", overflowY: "auto", overflowX: "auto", maxWidth: "100%", whiteSpace: "nowrap" }}>
          <table className="table table-hover mb-0" style={{
            minWidth: "800px",
            backgroundColor: "#fff"
          }}>
            <thead style={{
              backgroundColor: "#007bff",
              color: "white",
              position: "sticky",
              top: "0",
              zIndex: "10"
            }}>
              <tr>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "200px"
                }}>
                  User Name
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={userName}
                    onChange={(e) => {
                      setUserName(e.target.value);
                      setPage(1);
                    }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "200px"
                }}>
                  Email
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setPage(1);
                    }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "150px"
                }}>
                  Contact Number
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={contactNumber}
                    onChange={(e) => {
                      setContactNumber(e.target.value);
                      setPage(1);
                    }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "150px"
                }}>
                  Aadhaar Number
                  <br />
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm mt-2"
                    value={aadhaarNumber}
                    onChange={(e) => {
                      setAadhaarNumber(e.target.value);
                      setPage(1);
                    }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  />
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "120px"
                }}>
                  Gender
                  <br />
                  <select
                    className="form-control form-control-sm mt-2"
                    value={gender}
                    onChange={(e) => {
                      setGender(e.target.value);
                      setPage(1);
                    }}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "12px",
                      color: "#333",
                      padding: "4px 8px"
                    }}
                  >
                    <option value="">All</option>
                    {genders.map((g) => (
                      <option key={g.genderId} value={g.gender}>
                        {g.gender}
                      </option>
                    ))}
                  </select>
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minWidth: "150px"
                }}>
                  Role Name
                </th>
                <th style={{
                  padding: "15px 12px",
                  fontWeight: "600",
                  fontSize: "13px",
                  textAlign: "center",
                  minWidth: "150px"
                }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "13px" }}>
              {alldata.map((data, index) => (
                <tr key={index} style={{
                  backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#ffffff",
                  borderBottom: "1px solid #dee2e6"
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#e3f2fd";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#f8f9fa" : "#ffffff";
                  }}>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6",
                    fontWeight: "500"
                  }}>
                    {data.userName}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.email}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.contactNumber}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.aadhaarNumber}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.gender}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    color: "#495057",
                    borderRight: "1px solid #dee2e6"
                  }}>
                    {data.roleName}
                  </td>
                  <td style={{
                    padding: "12px 8px",
                    textAlign: "center"
                  }}>
                    <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => handleView(data)}
                        title="View"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleEdit(data.userId)}
                        title="Edit"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaRegEdit />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteClick(data)}
                        title="Delete"
                        style={{
                          padding: "4px 8px",
                          fontSize: "12px"
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className='d-flex justify-content-between pb-0'>
          <div className='p-2'>
            Showing {startItem} to {endItem} of {totalItems} items
          </div>
          <div className='p-2'>
            <Pagination className="mb-0">
              <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
              {renderPaginationItems()}
              <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalpage} />
              <Pagination.Last onClick={() => handlePageChange(totalpage)} disabled={page === totalpage} />
            </Pagination>
          </div>
        </div>

        {/* View Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>User Management</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>User Name:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.userName}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Email:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.email}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Contact Number:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.contactNumber}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Aadhaar Number:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.aadhaarNumber}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Gender:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.gender}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="textSecondary">
                    <strong>Role Name:</strong>
                  </Typography>
                  <Typography variant="body1">{selectedRow.roleName}</Typography>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary" variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete}>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this user?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDelete} color="primary">Cancel</Button>
            <Button onClick={handleDelete} color="primary">Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};

export default User;